import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { useLocation } from "wouter";
import { Button } from "../components/Button";
import { Card } from "../components/Card";
import { EmptyState } from "../components/EmptyState";
import { Field } from "../components/Field";
import { Modal } from "../components/Modal";
import { Skeleton } from "../components/Spinner";
import { useToast } from "../components/Toast";
import { ApiError, apiGet, apiSend, serverBesked } from "../lib/customFetch";
import { notifyFeaturesUpdated } from "../lib/useFeatures";
import { notifyMeUpdated, type Me } from "../lib/useMe";
import { KioskAdminCard } from "./KioskAdminCard";
import styles from "./IndstillingerPage.module.css";

interface PauseIndstillinger {
  aktiv: boolean;
  taerskelMinutter: number;
  minutter: number;
}

interface Settings {
  firmanavn: string;
  pause: PauseIndstillinger;
  projekter: { aktiv: boolean };
  loen: LoenSettings;
}

interface LoenSettings {
  cvr: string | null;
  mappings: Record<Loenkategori, string>;
}

type Loenkategori = "arbejdstid" | "ferie" | "sygdom" | "fri";

interface FeatureToggle {
  key: string;
  name: string;
  description: string;
  navPlacement: "main" | "settings" | "none";
  defaultEnabled: boolean;
  enabled: boolean;
}

type LoadState =
  | { status: "loading" }
  | {
      status: "ok";
      firmanavn: string;
      navn: string;
      rolle: Me["rolle"];
      pause: PauseIndstillinger;
      projekterAktiv: boolean;
      loen: LoenSettings;
      features: FeatureToggle[];
    }
  | { status: "failed" };

/**
 * Virksomhedsindstillinger (CORE, admin): firmanavn nu — kommende
 * tenant-regler (pauseregler m.v.) samles her. Plus chefens egen profil.
 */
export function IndstillingerPage() {
  const [state, setState] = useState<LoadState>({ status: "loading" });

  const hent = () => {
    setState({ status: "loading" });
    Promise.all([
      apiGet<Settings>("/api/settings"),
      apiGet<Me>("/api/me"),
      apiGet<FeatureToggle[]>("/api/settings/features"),
    ])
      .then(([settings, me, features]) =>
        setState({
          status: "ok",
          firmanavn: settings.firmanavn,
          navn: me.navn,
          rolle: me.rolle,
          pause: settings.pause,
          projekterAktiv: settings.projekter.aktiv,
          loen: settings.loen,
          features,
        }),
      )
      .catch(() => setState({ status: "failed" }));
  };

  useEffect(hent, []);

  if (state.status === "loading") {
    return (
      <div className={styles.side} aria-hidden="true">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} height="10rem" />
        ))}
      </div>
    );
  }

  if (state.status === "failed") {
    return (
      <Card>
        <EmptyState
          title="Kunne ikke hente indstillinger"
          description="Tjek din forbindelse og prøv igen."
          cta={<Button onClick={hent}>Prøv igen</Button>}
        />
      </Card>
    );
  }

  const clockinEnabled =
    state.features.find((feature) => feature.key === "clockin")?.enabled ??
    false;
  const featureChanged = (updated: FeatureToggle) => {
    setState((current) =>
      current.status !== "ok"
        ? current
        : {
            ...current,
            features: current.features.map((feature) =>
              feature.key === updated.key ? updated : feature,
            ),
          },
    );
  };

  return (
    <div className={styles.side}>
      <header className={styles.sideHeader}>
        <p className={styles.eyebrow}>Administration</p>
        <h1>Indstillinger</h1>
        <p>
          Tilpas virksomheden og arbejdsdagen. Vælg et område for at gå
          direkte til det, du skal ændre.
        </p>
      </header>

      <nav className={styles.omraadeNav} aria-label="Indstillingsområder">
        <a href="#virksomhed">
          <strong>Virksomhed</strong>
          <span>Firma og profil</span>
        </a>
        <a href="#arbejdsdag">
          <strong>Arbejdsdag</strong>
          <span>Funktioner og tid</span>
        </a>
        {clockinEnabled && (
          <a href="#loen">
            <strong>Lønfil</strong>
            <span>Danløn-format</span>
          </a>
        )}
        {clockinEnabled && (
          <a href="#arbejdsgange">
            <strong>Arbejdsgange</strong>
            <span>Projekter og kiosk</span>
          </a>
        )}
        <a href="#konto">
          <strong>Konto</strong>
          <span>Adgangskode og sikkerhed</span>
        </a>
      </nav>

      <IndstillingsSektion
        id="virksomhed"
        nummer="01"
        titel="Virksomhed og profil"
        beskrivelse="De navne, der møder medarbejderne og vises i administrationen."
      >
        <VirksomhedCard firmanavn={state.firmanavn} />
        <ProfilCard navn={state.navn} />
      </IndstillingsSektion>

      <IndstillingsSektion
        id="arbejdsdag"
        nummer="02"
        titel="Arbejdsdag og funktioner"
        beskrivelse="Vælg hvad virksomheden bruger, og hvordan arbejdstiden opgøres."
      >
        <FeatureTogglesCard
          features={state.features}
          onChanged={featureChanged}
        />
        {clockinEnabled && <PauseCard pause={state.pause} />}
      </IndstillingsSektion>

      {clockinEnabled && (
        <IndstillingsSektion
          id="loen"
          nummer="03"
          titel="Lønfil"
          beskrivelse="Vælg præcist hvad der skal med, før godkendte timer hentes til løn."
        >
          <LoenCard loen={state.loen} />
        </IndstillingsSektion>
      )}

      {clockinEnabled && (
        <IndstillingsSektion
          id="arbejdsgange"
          nummer="04"
          titel="Projekter og kiosk"
          beskrivelse="Knyt timer til arbejdet, eller gør en fælles skærm klar til holdet."
        >
          <ProjekterCard aktiv={state.projekterAktiv} />
          <KioskAdminCard />
        </IndstillingsSektion>
      )}

      <IndstillingsSektion
        id="konto"
        nummer={clockinEnabled ? "05" : "03"}
        titel="Konto og sikkerhed"
        beskrivelse="Beskyt din adgang. Konto-deaktivering er tydeligt adskilt nedenfor."
      >
        <PasswordCard />
        <div className={styles.fareSlot}>
          {state.rolle === "ejer" && <DeactivateAccountCard />}
        </div>
      </IndstillingsSektion>

      <div className={styles.mereSlot}>
        <UpsellCard />
      </div>
    </div>
  );
}

const LOENKATEGORIER: readonly {
  key: Loenkategori;
  label: string;
  hint: string;
}[] = [
  { key: "arbejdstid", label: "Arbejdstid", hint: "Godkendte nettotimer" },
  { key: "ferie", label: "Ferie", hint: "Planlagt tid på feriedage" },
  { key: "sygdom", label: "Sygdom", hint: "Planlagt tid på sygedage" },
  { key: "fri", label: "Fri", hint: "Planlagt tid på andre fridage" },
];

function LoenCard({ loen }: { loen: LoenSettings }) {
  const toast = useToast();
  const [cvr, setCvr] = useState(loen.cvr ?? "");
  const [mappings, setMappings] = useState(loen.mappings);
  const [busy, setBusy] = useState(false);
  const [fejl, setFejl] = useState<string | null>(null);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (cvr !== "" && !/^[0-9]{8}$/.test(cvr)) {
      setFejl("CVR skal bestå af præcis 8 cifre.");
      return;
    }
    const ugyldig = LOENKATEGORIER.find(
      ({ key }) =>
        mappings[key] !== "udelad" && !/^[0-9]{2}$/.test(mappings[key]),
    );
    if (ugyldig !== undefined) {
      setFejl(`${ugyldig.label}: Løntypen skal være 2 cifre.`);
      return;
    }
    setBusy(true);
    setFejl(null);
    apiSend<Settings>("PATCH", "/api/settings", {
      loen: { cvr: cvr === "" ? null : cvr, mappings },
    })
      .then((updated) => {
        setCvr(updated.loen.cvr ?? "");
        setMappings(updated.loen.mappings);
        toast.show("Lønfilens opsætning er gemt.", "success");
      })
      .catch((error: unknown) =>
        setFejl(
          (error instanceof ApiError ? serverBesked(error) : null) ??
            "Kunne ikke gemme lønopsætningen — prøv igen.",
        ),
      )
      .finally(() => setBusy(false));
  };

  const setValg = (key: Loenkategori, valg: "udelad" | "loentype") => {
    setMappings((current) => ({
      ...current,
      [key]: valg === "udelad" ? "udelad" : "",
    }));
  };

  return (
    <Card title="Danløn-format">
      <form className={styles.form} onSubmit={submit}>
        <p className={styles.hint}>
          AppSmeden vælger ikke løntyper for jer. Find de rigtige tocifrede
          koder i Danløn, og vælg ellers <strong>Udelad fra lønfil</strong>.
          Salary kan også importere Danløn-formatet.
        </p>
        <Field
          label="CVR-nummer"
          inputMode="numeric"
          maxLength={8}
          placeholder="8 cifre"
          hint="Skal være det samme CVR som virksomheden i lønsystemet."
          value={cvr}
          onChange={(event) => setCvr(event.target.value)}
        />
        <div className={styles.loenMappings}>
          {LOENKATEGORIER.map(({ key, label, hint }) => {
            const udeladt = mappings[key] === "udelad";
            return (
              <div key={key} className={styles.loenMapping}>
                <div className={styles.loenMappingTekst}>
                  <strong>{label}</strong>
                  <span>{hint}</span>
                </div>
                <label>
                  <span className="visually-hidden">{label}: valg</span>
                  <select
                    aria-label={`${label}: valg`}
                    value={udeladt ? "udelad" : "loentype"}
                    onChange={(event) =>
                      setValg(key, event.target.value as "udelad" | "loentype")
                    }
                  >
                    <option value="udelad">Udelad fra lønfil</option>
                    <option value="loentype">Brug løntype</option>
                  </select>
                </label>
                {!udeladt && (
                  <Field
                    label={`${label}: Danløn-løntype`}
                    inputMode="numeric"
                    maxLength={2}
                    placeholder="Fx 01"
                    value={mappings[key]}
                    onChange={(event) =>
                      setMappings((current) => ({
                        ...current,
                        [key]: event.target.value,
                      }))
                    }
                  />
                )}
              </div>
            );
          })}
        </div>
        {fejl !== null && (
          <p className={styles.fejl} role="alert">
            {fejl}
          </p>
        )}
        <Button type="submit" disabled={busy}>
          {busy ? "Gemmer…" : "Gem lønopsætning"}
        </Button>
      </form>
    </Card>
  );
}

function IndstillingsSektion({
  id,
  nummer,
  titel,
  beskrivelse,
  children,
}: {
  id: string;
  nummer: string;
  titel: string;
  beskrivelse: string;
  children: ReactNode;
}) {
  const headingId = `${id}-titel`;
  return (
    <section
      id={id}
      className={styles.omraade}
      aria-labelledby={headingId}
    >
      <header className={styles.omraadeHeader}>
        <span aria-hidden="true">{nummer}</span>
        <div>
          <h2 id={headingId}>{titel}</h2>
          <p>{beskrivelse}</p>
        </div>
      </header>
      <div className={styles.omraadeKort}>{children}</div>
    </section>
  );
}

function FeatureTogglesCard({
  features,
  onChanged,
}: {
  features: FeatureToggle[];
  onChanged: (updated: FeatureToggle) => void;
}) {
  const toast = useToast();
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [fejl, setFejl] = useState<string | null>(null);
  const synlige = features.filter((feature) => feature.navPlacement !== "none");

  const toggle = (feature: FeatureToggle, enabled: boolean) => {
    if (
      !enabled &&
      !window.confirm(
        `Slå ${feature.name} fra? Funktionen forsvinder fra navigationen og kan ikke bruges, før den slås til igen.`,
      )
    ) {
      return;
    }
    setBusyKey(feature.key);
    setFejl(null);
    apiSend<FeatureToggle>(
      "PATCH",
      `/api/settings/features/${feature.key}`,
      { enabled },
    )
      .then((updated) => {
        onChanged(updated);
        setBusyKey(null);
        notifyFeaturesUpdated();
        toast.show(
          `${feature.name} er slået ${enabled ? "til" : "fra"}.`,
          "success",
        );
      })
      .catch(() => {
        setBusyKey(null);
        setFejl("Kunne ikke ændre funktionen — prøv igen.");
      });
  };

  return (
    <Card title="Funktioner">
      <div className={styles.form}>
        <p className={styles.hint}>
          Slå virksomhedens hovedfunktioner til eller fra. En slået fra
          funktion forsvinder fra navigationen og kan ikke bruges, før den slås til igen.
        </p>
        <ul className={styles.featureListe}>
          {synlige.map((feature) => (
            <li key={feature.key} className={styles.featureRaekke}>
              <div>
                <strong>{feature.name}</strong>
                <p>{feature.description}</p>
              </div>
              <label className={styles.tjekRaekke}>
                <input
                  type="checkbox"
                  aria-label={`Brug ${feature.name}`}
                  checked={feature.enabled}
                  disabled={busyKey === feature.key}
                  onChange={(event) => toggle(feature, event.target.checked)}
                />
                {feature.enabled ? "Til" : "Fra"}
              </label>
            </li>
          ))}
        </ul>
        {fejl !== null && (
          <p className={styles.fejl} role="alert">
            {fejl}
          </p>
        )}
      </div>
    </Card>
  );
}

/**
 * Upsell (fase 3d) — håndværker-til-håndværker, aldrig pushy: fortæller
 * hvad et skræddersyet system kan, og peger på appsmeden.dk.
 */
function UpsellCard() {
  return (
    <Card title="Hvad kan jeres system mere?">
      <div className={styles.upsell}>
        <p>
          Tidsregistreringen her er gratis, og det bliver den ved med at
          være. Men når hverdagen vokser, bygger vi videre — skræddersyet
          til jeres måde at arbejde på:
        </p>
        <ul className={styles.upsellListe}>
          <li>Vagtplan — læg planen dér, hvor timerne alligevel registreres</li>
          <li>GPS-stempling — se hvor der stemples ind, fx på skiftende pladser</li>
        </ul>
        <p>
          Alt sammen noget vi bygger til jer — ikke moduler fra hylden.{" "}
          <a href="https://appsmeden.dk" target="_blank" rel="noopener noreferrer">
            Snak med os på appsmeden.dk
          </a>
          , så finder vi ud af, hvad der giver mening.
        </p>
      </div>
    </Card>
  );
}

function PauseCard({ pause: initial }: { pause: PauseIndstillinger }) {
  const toast = useToast();
  const [aktiv, setAktiv] = useState(initial.aktiv);
  const [taerskel, setTaerskel] = useState(String(initial.taerskelMinutter));
  const [minutter, setMinutter] = useState(String(initial.minutter));
  const [fejl, setFejl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const taerskelTal = Number(taerskel);
    const minutterTal = Number(minutter);
    if (!Number.isInteger(taerskelTal) || taerskelTal < 1 || taerskelTal > 1440) {
      setFejl("Tærsklen skal være mellem 1 og 1440 minutter.");
      return;
    }
    if (!Number.isInteger(minutterTal) || minutterTal < 0 || minutterTal > 480) {
      setFejl("Pausen skal være mellem 0 og 480 minutter.");
      return;
    }
    setBusy(true);
    setFejl(null);
    apiSend<Settings>("PATCH", "/api/settings", {
      pause: { aktiv, taerskelMinutter: taerskelTal, minutter: minutterTal },
    })
      .then(() => {
        setBusy(false);
        toast.show("Pausereglerne er gemt.", "success");
      })
      .catch(() => {
        setBusy(false);
        setFejl("Kunne ikke gemme — prøv igen.");
      });
  };

  return (
    <Card title="Pauseregler">
      <form className={styles.form} onSubmit={submit}>
        <p className={styles.hint}>
          Automatisk pausefradrag i opgørelser og eksport — fx 8 t 12 m −
          30 m pause = 7 t 42 m. Selve stemplingerne røres aldrig, og du kan
          rette pausen for en enkelt dag i ugeoversigten.
        </p>
        <label className={styles.tjekRaekke}>
          <input
            type="checkbox"
            checked={aktiv}
            onChange={(e) => setAktiv(e.target.checked)}
          />
          Træk automatisk pause fra
        </label>
        <div className={styles.talFelter}>
          <Field
            label="Ved arbejdsdag længere end (minutter)"
            type="number"
            min={1}
            max={1440}
            value={taerskel}
            disabled={!aktiv}
            onChange={(e) => setTaerskel(e.target.value)}
            hint="360 minutter = 6 timer"
          />
          <Field
            label="Pause der trækkes fra (minutter)"
            type="number"
            min={0}
            max={480}
            value={minutter}
            disabled={!aktiv}
            onChange={(e) => setMinutter(e.target.value)}
          />
        </div>
        {fejl !== null && (
          <p className={styles.fejl} role="alert">
            {fejl}
          </p>
        )}
        <div>
          <Button type="submit" disabled={busy}>
            {busy ? "Gemmer…" : "Gem pauseregler"}
          </Button>
        </div>
      </form>
    </Card>
  );
}

interface Projekt {
  id: string;
  navn: string;
  kundenavn: string | null;
  aktiv: boolean;
}

/**
 * Projekter (M-11): tenant-toggle + simpel CRUD. Featuren er slået fra som
 * default; slås den til, dukker projekt-dropdown'en op på medarbejdernes
 * stemple-skærm og projekt-/kunde-kolonner i opgørelser og eksport. "Én
 * dropdown, ikke et PSA-system" — kunden er blot et frit tekstfelt.
 */
function ProjekterCard({ aktiv: initial }: { aktiv: boolean }) {
  const toast = useToast();
  const [aktiv, setAktiv] = useState(initial);
  const [toggleBusy, setToggleBusy] = useState(false);
  const [projekter, setProjekter] = useState<Projekt[] | null>(null);
  const [listeFejl, setListeFejl] = useState(false);
  // Opret/redigér-formular: redigerId = null ⇒ opret-tilstand.
  const [redigerId, setRedigerId] = useState<string | null>(null);
  const [navn, setNavn] = useState("");
  const [kundenavn, setKundenavn] = useState("");
  const [formFejl, setFormFejl] = useState<string | null>(null);
  const [formBusy, setFormBusy] = useState(false);

  const hentListe = () => {
    apiGet<Projekt[]>("/api/clockin/admin/projekter")
      .then((liste) => {
        setProjekter(liste);
        setListeFejl(false);
      })
      .catch(() => setListeFejl(true));
  };

  useEffect(() => {
    if (aktiv) hentListe();
  }, [aktiv]);

  const nulstilForm = () => {
    setRedigerId(null);
    setNavn("");
    setKundenavn("");
    setFormFejl(null);
  };

  const toggle = (nyAktiv: boolean) => {
    setToggleBusy(true);
    apiSend<Settings>("PATCH", "/api/settings", {
      projekter: { aktiv: nyAktiv },
    })
      .then(() => {
        setToggleBusy(false);
        setAktiv(nyAktiv);
        toast.show(
          nyAktiv ? "Projekter er slået til." : "Projekter er slået fra.",
          "success",
        );
      })
      .catch(() => {
        setToggleBusy(false);
        toast.show("Kunne ikke gemme — prøv igen.", "error");
      });
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (navn.trim().length === 0) {
      setFormFejl("Angiv et projektnavn.");
      return;
    }
    setFormBusy(true);
    setFormFejl(null);
    const body = {
      navn: navn.trim(),
      kundenavn: kundenavn.trim().length > 0 ? kundenavn.trim() : null,
    };
    const kald =
      redigerId === null
        ? apiSend<Projekt>("POST", "/api/clockin/admin/projekter", body)
        : apiSend<Projekt>(
            "PATCH",
            `/api/clockin/admin/projekter/${redigerId}`,
            body,
          );
    kald
      .then(() => {
        setFormBusy(false);
        nulstilForm();
        hentListe();
        toast.show(
          redigerId === null ? "Projekt oprettet." : "Projekt opdateret.",
          "success",
        );
      })
      .catch((err: unknown) => {
        setFormBusy(false);
        setFormFejl(
          err instanceof ApiError && err.status === 409
            ? "Der findes allerede et aktivt projekt med det navn."
            : "Kunne ikke gemme — prøv igen.",
        );
      });
  };

  const saetAktiv = (p: Projekt, nyAktiv: boolean) => {
    apiSend<Projekt>("PATCH", `/api/clockin/admin/projekter/${p.id}`, {
      aktiv: nyAktiv,
    })
      .then(() => {
        hentListe();
        if (redigerId === p.id) nulstilForm();
      })
      .catch(() => toast.show("Kunne ikke gemme — prøv igen.", "error"));
  };

  const startRedigering = (p: Projekt) => {
    setRedigerId(p.id);
    setNavn(p.navn);
    setKundenavn(p.kundenavn ?? "");
    setFormFejl(null);
  };

  const aktive = (projekter ?? []).filter((p) => p.aktiv);
  const inaktive = (projekter ?? []).filter((p) => !p.aktiv);

  return (
    <Card title="Projekter">
      <div className={styles.form}>
        <p className={styles.hint}>
          Knyt timer til projekter og kunder. Når det er slået til, kan
          medarbejderne vælge projekt ved indstempling, og opgørelser og
          eksport bærer projekt- og kundekolonner. Medarbejderen vælger blot
          fra en kort liste — det er ikke et helt sagssystem.
        </p>
        <label className={styles.tjekRaekke}>
          <input
            type="checkbox"
            checked={aktiv}
            disabled={toggleBusy}
            onChange={(e) => toggle(e.target.checked)}
          />
          Brug projekter
        </label>

        {aktiv && (
          <>
            <form className={styles.form} onSubmit={submit}>
              <div className={styles.talFelter}>
                <Field
                  label="Projektnavn"
                  value={navn}
                  onChange={(e) => setNavn(e.target.value)}
                  maxLength={120}
                />
                <Field
                  label="Kunde (valgfri)"
                  value={kundenavn}
                  onChange={(e) => setKundenavn(e.target.value)}
                  maxLength={120}
                />
              </div>
              {formFejl !== null && (
                <p className={styles.fejl} role="alert">
                  {formFejl}
                </p>
              )}
              <div className={styles.projektFormKnapper}>
                <Button type="submit" disabled={formBusy}>
                  {formBusy
                    ? "Gemmer…"
                    : redigerId === null
                      ? "Tilføj projekt"
                      : "Gem ændringer"}
                </Button>
                {redigerId !== null && (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={nulstilForm}
                  >
                    Annullér
                  </Button>
                )}
              </div>
            </form>

            {listeFejl ? (
              <p className={styles.fejl} role="alert">
                Kunne ikke hente projekter — prøv at genindlæse.
              </p>
            ) : aktive.length === 0 && inaktive.length === 0 ? (
              <p className={styles.hint}>
                Ingen projekter endnu — tilføj det første ovenfor.
              </p>
            ) : (
              <ul className={styles.projektListe}>
                {[...aktive, ...inaktive].map((p) => (
                  <li
                    key={p.id}
                    className={[
                      styles.projektRaekke,
                      p.aktiv ? null : styles.projektInaktiv,
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    <div className={styles.projektInfo}>
                      <span className={styles.projektNavn}>{p.navn}</span>
                      {p.kundenavn !== null && (
                        <span className={styles.projektKunde}>
                          {p.kundenavn}
                        </span>
                      )}
                      {!p.aktiv && (
                        <span className={styles.projektBadge}>deaktiveret</span>
                      )}
                    </div>
                    <div className={styles.projektHandlinger}>
                      {p.aktiv ? (
                        <>
                          <Button
                            type="button"
                            variant="ghost"
                            onClick={() => startRedigering(p)}
                          >
                            Redigér
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            onClick={() => saetAktiv(p, false)}
                          >
                            Deaktivér
                          </Button>
                        </>
                      ) : (
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => saetAktiv(p, true)}
                        >
                          Genaktivér
                        </Button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </Card>
  );
}

function VirksomhedCard({ firmanavn: initial }: { firmanavn: string }) {
  const toast = useToast();
  const [firmanavn, setFirmanavn] = useState(initial);
  const [fejl, setFejl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (firmanavn.trim().length === 0) {
      setFejl("Angiv virksomhedens navn.");
      return;
    }
    setBusy(true);
    setFejl(null);
    apiSend<Settings>("PATCH", "/api/settings", {
      firmanavn: firmanavn.trim(),
    })
      .then((res) => {
        setBusy(false);
        setFirmanavn(res.firmanavn);
        notifyMeUpdated();
        toast.show("Virksomhedens navn er gemt.", "success");
      })
      .catch(() => {
        setBusy(false);
        setFejl("Kunne ikke gemme — prøv igen.");
      });
  };

  return (
    <Card title="Virksomhed">
      <form className={styles.form} onSubmit={submit}>
        <Field
          label="Firmanavn"
          value={firmanavn}
          onChange={(e) => setFirmanavn(e.target.value)}
          required
        />
        {fejl !== null && (
          <p className={styles.fejl} role="alert">
            {fejl}
          </p>
        )}
        <div>
          <Button type="submit" disabled={busy}>
            {busy ? "Gemmer…" : "Gem"}
          </Button>
        </div>
      </form>
    </Card>
  );
}

function ProfilCard({ navn: initial }: { navn: string }) {
  const toast = useToast();
  const [navn, setNavn] = useState(initial);
  const [fejl, setFejl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (navn.trim().length === 0) {
      setFejl("Angiv dit navn.");
      return;
    }
    setBusy(true);
    setFejl(null);
    apiSend<{ navn: string }>("PATCH", "/api/me", { navn: navn.trim() })
      .then((res) => {
        setBusy(false);
        setNavn(res.navn);
        notifyMeUpdated();
        toast.show("Dit navn er gemt.", "success");
      })
      .catch(() => {
        setBusy(false);
        setFejl("Kunne ikke gemme — prøv igen.");
      });
  };

  return (
    <Card title="Din profil">
      <form className={styles.form} onSubmit={submit}>
        <Field
          label="Navn"
          value={navn}
          onChange={(e) => setNavn(e.target.value)}
          required
        />
        {fejl !== null && (
          <p className={styles.fejl} role="alert">
            {fejl}
          </p>
        )}
        <div>
          <Button type="submit" disabled={busy}>
            {busy ? "Gemmer…" : "Gem"}
          </Button>
        </div>
      </form>
    </Card>
  );
}

export function PasswordCard() {
  const toast = useToast();
  const [nuvaerende, setNuvaerende] = useState("");
  const [nyt, setNyt] = useState("");
  const [gentag, setGentag] = useState("");
  const [fejl, setFejl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (nyt.length < 10) {
      setFejl("Den nye adgangskode skal være mindst 10 tegn.");
      return;
    }
    if (nyt !== gentag) {
      setFejl("De to nye adgangskoder er ikke ens.");
      return;
    }
    setBusy(true);
    setFejl(null);
    apiSend("POST", "/api/me/password", {
      nuvaerendePassword: nuvaerende,
      nytPassword: nyt,
    })
      .then(() => {
        setBusy(false);
        setNuvaerende("");
        setNyt("");
        setGentag("");
        toast.show("Adgangskoden er skiftet. Andre enheder er logget ud.", "success");
      })
      .catch((err: unknown) => {
        setBusy(false);
        setFejl(
          err instanceof ApiError && err.status === 403
            ? "Den nuværende adgangskode er forkert."
            : "Kunne ikke skifte adgangskoden — prøv igen.",
        );
      });
  };

  return (
    <Card title="Skift adgangskode">
      <form className={styles.form} onSubmit={submit}>
        <Field
          label="Nuværende adgangskode"
          type="password"
          autoComplete="current-password"
          value={nuvaerende}
          onChange={(e) => setNuvaerende(e.target.value)}
          required
        />
        <Field
          label="Ny adgangskode"
          type="password"
          autoComplete="new-password"
          hint="Mindst 10 tegn."
          value={nyt}
          onChange={(e) => setNyt(e.target.value)}
          required
        />
        <Field
          label="Gentag ny adgangskode"
          type="password"
          autoComplete="new-password"
          value={gentag}
          onChange={(e) => setGentag(e.target.value)}
          required
        />
        {fejl !== null && (
          <p className={styles.fejl} role="alert">
            {fejl}
          </p>
        )}
        <div>
          <Button type="submit" disabled={busy}>
            {busy ? "Skifter…" : "Skift adgangskode"}
          </Button>
        </div>
      </form>
    </Card>
  );
}

function DeactivateAccountCard() {
  const [, navigate] = useLocation();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [fejl, setFejl] = useState<string | null>(null);
  const inFlight = useRef(false);

  const close = () => {
    if (inFlight.current) return;
    setOpen(false);
    setFejl(null);
  };

  const deactivate = () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setFejl(null);
    apiSend("POST", "/api/me/deactivate")
      .then(() => {
        navigate("/login?konto=deaktiveret", { replace: true });
      })
      .catch(() => {
        inFlight.current = false;
        setBusy(false);
        setFejl("Kontoen kunne ikke deaktiveres — prøv igen.");
      });
  };

  return (
    <Card title="Deaktivér din konto">
      <div className={styles.farezone}>
        <p>
          Deaktivering logger dig straks ud på alle enheder. En anden ejer skal
          genaktivere kontoen, før du kan logge ind igen.
        </p>
        <div>
          <Button variant="destructive" onClick={() => setOpen(true)}>
            Deaktivér konto
          </Button>
        </div>
      </div>

      {open && (
        <Modal
          title="Deaktivér din konto?"
          onClose={close}
          closeDisabled={busy}
        >
          <div className={styles.bekraeftelse}>
            <p>
              Du bliver logget ud med det samme, og alle dine aktive enheder
              bliver logget ud. Virksomhedens data slettes ikke.
            </p>
            <p>
              For at få adgang igen skal en anden ejer genaktivere din konto.
            </p>
            {fejl !== null && (
              <p className={styles.fejl} role="alert">
                {fejl}
              </p>
            )}
            <div className={styles.bekraeftelseKnapper}>
              <Button variant="secondary" onClick={close} disabled={busy}>
                Annuller
              </Button>
              <Button
                variant="destructive"
                onClick={deactivate}
                disabled={busy}
              >
                {busy ? "Deaktiverer…" : "Ja, deaktivér min konto"}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </Card>
  );
}
